/**
 * Calendar schemas — TypeBox.
 *
 * Source of truth: docs/epics/epic-E2-external-integrations.md §2.B and the
 * Google Calendar v3 reference (https://developers.google.com/calendar/api/v3/reference/events).
 *
 * Constitution Article IV (No Invention): every property below traces to either
 * Google Calendar's documented event resource or to PRD §FR fields (sdr_meeting_id
 * extendedProperty for dedupe).
 *
 * Story 2.4 — CalendarService contracts.
 */
import { Type } from '@sinclair/typebox';
// ============================================================================
// Reusable primitives
// ============================================================================
const DateTime = Type.String({ format: 'date-time' });
const Email = Type.String({ format: 'email' });
/**
 * Calendar event datetime block. Mirrors Google's `EventDateTime` resource:
 * https://developers.google.com/calendar/api/v3/reference/events#resource
 *
 * `dateTime` is RFC3339 (with offset). When omitted, `date` (YYYY-MM-DD) drives
 * an all-day event — but the SDR flow always uses timed events.
 */
export const EventDateTime = Type.Object({
    dateTime: Type.Optional(DateTime),
    date: Type.Optional(Type.String({ format: 'date' })),
    timeZone: Type.Optional(Type.String()),
});
/**
 * Event attendee. `email` is required by Google; `responseStatus` is whichever
 * the API returned (or `needsAction` on create).
 */
export const EventAttendee = Type.Object({
    email: Email,
    displayName: Type.Optional(Type.String()),
    optional: Type.Optional(Type.Boolean()),
    responseStatus: Type.Optional(Type.Union([
        Type.Literal('needsAction'),
        Type.Literal('declined'),
        Type.Literal('tentative'),
        Type.Literal('accepted'),
    ])),
});
// ============================================================================
// Free / busy
// ============================================================================
/**
 * Single busy interval as Google Calendar v3 returns from `freebusy.query`.
 * Both fields are RFC3339 datetimes.
 */
export const TimeSlot = Type.Object({
    start: DateTime,
    end: DateTime,
});
/**
 * Output of `getFreeBusy(calendarId, timeMin, timeMax)`. Mirrors what the
 * service returns (a flattened view of Google's `freebusy.calendars` map).
 */
export const FreeBusyResponse = Type.Object({
    calendarId: Type.String({ minLength: 1 }),
    timeMin: DateTime,
    timeMax: DateTime,
    timeZone: Type.String(),
    busy: Type.Array(TimeSlot),
});
// ============================================================================
// Event resource (subset we touch)
// ============================================================================
/**
 * Calendar event payload returned by the service after `createEvent` /
 * `updateEvent`. We model only the fields the SDR flow consumes — Google
 * returns ~40 — keeping the surface tight protects callers from drift.
 *
 * `extendedProperties.private.sdr_meeting_id` is OUR dedupe key (PRD §FR9
 * meeting idempotency). Google preserves arbitrary string keys here.
 */
export const CalendarEvent = Type.Object({
    id: Type.String({ minLength: 1 }),
    status: Type.Optional(Type.Union([
        Type.Literal('confirmed'),
        Type.Literal('tentative'),
        Type.Literal('cancelled'),
    ])),
    htmlLink: Type.Optional(Type.String({ format: 'uri' })),
    summary: Type.Optional(Type.String()),
    description: Type.Optional(Type.String()),
    location: Type.Optional(Type.String()),
    start: EventDateTime,
    end: EventDateTime,
    attendees: Type.Optional(Type.Array(EventAttendee)),
    organizer: Type.Optional(Type.Object({
        email: Type.Optional(Email),
        displayName: Type.Optional(Type.String()),
    })),
    extendedProperties: Type.Optional(Type.Object({
        private: Type.Optional(Type.Record(Type.String(), Type.String())),
        shared: Type.Optional(Type.Record(Type.String(), Type.String())),
    })),
    iCalUID: Type.Optional(Type.String()),
    created: Type.Optional(DateTime),
    updated: Type.Optional(DateTime),
});
/**
 * Input shape accepted by `createEvent` / `updateEvent`. Mandatory fields:
 * `summary`, `start`, `end`. `sdrMeetingId` becomes the
 * `extendedProperties.private.sdr_meeting_id` value used for dedupe.
 */
export const CreateEventRequest = Type.Object({
    summary: Type.String({ minLength: 1 }),
    description: Type.Optional(Type.String()),
    location: Type.Optional(Type.String()),
    start: EventDateTime,
    end: EventDateTime,
    attendees: Type.Optional(Type.Array(EventAttendee)),
    /** Internal SDR meeting UUID — written to extendedProperties.private. */
    sdrMeetingId: Type.Optional(Type.String({ format: 'uuid' })),
    /** Free-form bag of additional private extended properties. */
    privateProperties: Type.Optional(Type.Record(Type.String(), Type.String())),
    /** When true, Google sends invite emails to attendees. Default false. */
    sendUpdates: Type.Optional(Type.Union([Type.Literal('all'), Type.Literal('externalOnly'), Type.Literal('none')])),
});
//# sourceMappingURL=calendar.js.map